import { call, select, takeLatest } from 'redux-saga/effects';
import { showErrorToast } from '../tools';
import { Resp, dispatch } from '../helpers';
import { actions } from './queries.slice';
import { resolveQueriesTransport } from './queries.transport';
import { QueriesFetchAction, QueriesFilters, QueryFetchAction } from './queries.types';
import { selectAuthView } from '@restore/auth/auth.slice';
import { RootState } from '@restore/store';
import { PaginationAndSortType } from '@restore/paginationAndSort/paginationAndSort.types';

const NO_TRANSPORT_ERROR = 'Queries are not available for the current role';

export function* fetchQueriesWorker(action: QueriesFetchAction) {
  try {
    const { selectedRole }: ReturnType<typeof selectAuthView> = yield select(
      selectAuthView
    );

    const transport = resolveQueriesTransport(selectedRole);

    if (!transport) {
      throw new Error(NO_TRANSPORT_ERROR);
    }

    const localFilters = (yield select(
      (state: RootState) => state.queries?.filters || {}
    )) as QueriesFilters;

    const pagSort = (yield select(
      (state: RootState) =>
        state.paginationAndSort?.lists?.queries || {
          currentPage: 1,
          currentPageSize: 10,
        }
    )) as PaginationAndSortType;

    const filters = {
      ...localFilters,
      page: pagSort.currentPage,
      size: pagSort.currentPageSize,
      sort: pagSort.sortBy,
    };

    const response: Resp<typeof transport.list> = yield call(
      transport.list,
      action.payload,
      filters
    );

    yield dispatch(actions.fetchQueriesSuccess(response.data));
  } catch (error) {
    yield* showErrorToast(error, 'Failed to fetch queries');
    yield dispatch(actions.fetchQueriesFailure(error as Error));
  }
}

export function* fetchCurrentQueryWorker(action: QueryFetchAction) {
  try {
    const { selectedRole }: ReturnType<typeof selectAuthView> = yield select(
      selectAuthView
    );

    const transport = resolveQueriesTransport(selectedRole);

    if (!transport) {
      throw new Error(NO_TRANSPORT_ERROR);
    }

    const { queryId, clinicalTrialId } = action.payload;

    const response: Resp<typeof transport.byId> = yield call(
      transport.byId,
      queryId,
      clinicalTrialId
    );

    yield dispatch(actions.fetchCurrentQuerySuccess(response.data));
  } catch (error) {
    yield* showErrorToast(error, 'Failed to fetch query');
    yield dispatch(actions.fetchCurrentQueryFailure(error as Error));
  }
}

export function* queriesSaga() {
  yield takeLatest(actions.fetchQueries.type, fetchQueriesWorker);
  yield takeLatest(actions.fetchCurrentQuery.type, fetchCurrentQueryWorker);
}
